//---------------------------------------------------------------------------------------------------
//  ?                              USE WEBSOCKET HOOK
//  @author         :  DeeTrade Team  
//  @createdOn      :  06/19/2025
//  @description    :  React hook for ultra-fast WebSocket integration with trading platform
//---------------------------------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from 'react';
import WebSocketManager from '../websocket/WebSocketManager';
import { MESSAGE_TYPES } from '../websocket/protocol/UltraFastProtocol';

/**
 * Ultra-fast WebSocket React hook for trading platform
 * Provides connection state, messaging, and subscription management
 * Optimized for <0.5ms latency with automatic reconnection
 */
export function useWebSocket(options = {}) {
  const {
    page_id = 'unknown',
    autoConnect = true,
    enableMetrics = true,
    channels = [],
    onMessage = null,
    onError = null,
    onConnect = null,
    onDisconnect = null
  } = options;

  // Connection state
  const [connectionState, setConnectionState] = useState('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    latency: null,
    messageCount: 0,
    reconnectCount: 0,
    uptime: 0
  });

  // Message handling
  const [lastMessage, setLastMessage] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const subscriptionsRef = useRef(new Set());
  const handlersRef = useRef(new Map());
  const pageIdRef = useRef(page_id);

  // Performance tracking
  const metricsIntervalRef = useRef(null);
  const messageCountRef = useRef(0);

  // Update page ID reference
  useEffect(() => {
    pageIdRef.current = page_id;
  }, [page_id]);

  /**
   * Handle connection state changes
   */
  useEffect(() => {
    const handleConnection = (data) => {
      setConnectionState(data.state);
      setIsConnected(data.state === 'connected');
      setError(null);
      
      if (data.state === 'connected' && onConnect) {
        onConnect(data);
      }
    };

    const handleDisconnection = (data) => {
      setConnectionState('disconnected');
      setIsConnected(false);
      
      if (onDisconnect) {
        onDisconnect(data);
      }
    };

    const handleError = (error) => {
      setError(error);
      setConnectionState('error');
      setIsConnected(false);
      
      if (onError) {
        onError(error);
      }
    };

    const handleReconnect = (data) => {
      setMetrics(prev => ({
        ...prev,
        reconnectCount: prev.reconnectCount + 1
      }));
    };

    // Register event listeners
    WebSocketManager.on('connection', handleConnection);
    WebSocketManager.on('disconnection', handleDisconnection);
    WebSocketManager.on('error', handleError);
    WebSocketManager.on('reconnect', handleReconnect);

    // Sync initial state with manager
    // This is critical if the connection is already established before this hook mounts
    if (WebSocketManager.connectionState === 'connected') {
      handleConnection({ state: 'connected' });
    } else if (WebSocketManager.connectionState === 'connecting') {
      setConnectionState('connecting');
    }

    // Auto-connect if enabled
    // FIX: Disabled auto-connect here to prevent race conditions.
    // WebSocketProvider is responsible for the single global connection after auth.
    /*
    if (autoConnect && WebSocketManager.connectionState === 'disconnected') {
      WebSocketManager.connect().catch(error => {
        setError(error);
        console.error('WebSocket connection failed:', error);
      });
    }
    */

    return () => {
      WebSocketManager.off('connection', handleConnection);
      WebSocketManager.off('disconnection', handleDisconnection);
      WebSocketManager.off('error', handleError);
      WebSocketManager.off('reconnect', handleReconnect);
    };
  }, [autoConnect, connectionState, onConnect, onDisconnect, onError]);

  /**
   * Set up metrics collection
   */
  useEffect(() => {
    if (enableMetrics && isConnected) {
      metricsIntervalRef.current = setInterval(() => {
        const wsMetrics = WebSocketManager.getMetrics();
        setMetrics(prev => ({
          latency: wsMetrics.lastLatency,
          messageCount: messageCountRef.current,
          reconnectCount: wsMetrics.reconnectCount,
          uptime: wsMetrics.uptime,
          avgLatency: wsMetrics.avgLatency
        }));
      }, 1000);
    } else if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
      metricsIntervalRef.current = null;
    }

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [enableMetrics, isConnected]);

  /**
   * Subscribe to initial channels
   */
  useEffect(() => {
    if (isConnected && channels.length > 0) {
      channels.forEach(channel => {
        WebSocketManager.subscribe(channel, (message) => {
          messageCountRef.current++;
          setLastMessage(message);
          if (onMessage) {
            onMessage(message);
          }
        });
      });
    }
  }, [isConnected, channels, onMessage]);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(async () => {
    try {
      await WebSocketManager.connect();
    } catch (error) {
      setError(error);
      console.error('WebSocket connection failed:', error);
    }
  }, []);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    // Unsubscribe from all channels
    subscriptionsRef.current.forEach(unsubscribeFunc => {
      if (typeof unsubscribeFunc === 'function') {
        unsubscribeFunc();
      }
    });
    subscriptionsRef.current.clear();
    
    WebSocketManager.disconnect();
  }, []);

  /**
   * Send message with page context
   */
  const sendMessage = useCallback((message, channel = 'default') => {
    if (!isConnected) {
      console.warn('Cannot send message: WebSocket not connected');
      return false;
    }

    const messageWithContext = {
      ...message,
      page_id: pageIdRef.current,
      client_timestamp: performance.now()
    };

    try {
      WebSocketManager.sendMessage(messageWithContext, channel);
      messageCountRef.current++;
      return true;
    } catch (error) {
      console.error('Failed to send message:', error);
      setError(error);
      return false;
    }
  }, [isConnected]);

  /**
   * Subscribe to channel with message handler
   */
  const subscribe = useCallback((channel, handler = null) => {
    const messageHandler = (message) => {
      messageCountRef.current++;
      setLastMessage(message);
      
      // Update message history (keep last 100 messages)
      setMessageHistory(prev => {
        const newHistory = [message, ...prev];
        return newHistory.slice(0, 100);
      });
      
      // Call specific handler if provided
      if (handler) {
        handler(message);
      }
      
      // Call global message handler
      if (onMessage) {
        onMessage(message);
      }
    };

    // Subscribe to channel
    const unsubscribeFunc = WebSocketManager.subscribe(channel, messageHandler);
    subscriptionsRef.current.add(unsubscribeFunc);
    handlersRef.current.set(channel, messageHandler);

    // Return unsubscribe function
    return () => {
      subscriptionsRef.current.delete(unsubscribeFunc);
      handlersRef.current.delete(channel);
      unsubscribeFunc();
    };
  }, [onMessage]);

  /**
   * Unsubscribe from channel
   */
  const unsubscribe = useCallback((channel) => {
    const handler = handlersRef.current.get(channel);
    if (handler) {
      WebSocketManager.unsubscribe(channel, handler);
      handlersRef.current.delete(channel);
    }
  }, []);

  /**
   * Subscribe to topic (modern protocol for Redis-bridged messages)
   */
  const subscribeTopic = useCallback((topic, handler, options = {}) => {
    // Call manager's subscribeTopic for proper topic-based subscription
    const unsubscribeFunc = WebSocketManager.subscribeTopic(topic, handler, options);
    // Track for cleanup
    subscriptionsRef.current.add(unsubscribeFunc);
    
    return () => {
      subscriptionsRef.current.delete(unsubscribeFunc);
      unsubscribeFunc();
    };
  }, []);

  /**
   * Send trading-specific message
   */
  const sendTradingMessage = useCallback((type, data) => {
    const tradingMessage = {
      type: type,
      timestamp: performance.now(),
      page_id: pageIdRef.current,
      ...data
    };

    return sendMessage(tradingMessage, 'trading');
  }, [sendMessage]);

  /**
   * Subscribe to market data for specific symbol
   */
  const subscribeToMarketData = useCallback((symbol, handler) => {
    const channel = `market_data_${symbol}`;
    const unsubscribe = subscribe(channel, handler);
    
    // Send subscription request
    sendMessage({
      type: MESSAGE_TYPES.SUBSCRIBE,
      symbol: symbol,
      data_type: 'market_data'
    }, channel);
    
    return unsubscribe;
  }, [subscribe, sendMessage]);

  /**
   * Subscribe to order updates
   */
  const subscribeToOrderUpdates = useCallback((handler) => {
    const channel = 'order_updates';
    const unsubscribe = subscribe(channel, handler);
    
    // Send subscription request
    sendMessage({
      type: MESSAGE_TYPES.SUBSCRIBE,
      data_type: 'order_updates'
    }, channel);
    
    return unsubscribe;
  }, [subscribe, sendMessage]);

  /**
   * Subscribe to portfolio updates
   */
  const subscribeToPortfolioUpdates = useCallback((handler) => {
    const channel = 'portfolio_updates';
    const unsubscribe = subscribe(channel, handler);
    
    // Send subscription request
    sendMessage({
      type: MESSAGE_TYPES.SUBSCRIBE,
      data_type: 'portfolio_updates'
    }, channel);
    
    return unsubscribe;
  }, [subscribe, sendMessage]);

  /**
   * Send ping for latency measurement
   */
  const ping = useCallback(() => {
    if (!isConnected) return;
    
    const timestamp = performance.now();
    sendMessage({
      type: MESSAGE_TYPES.PING,
      timestamp: timestamp
    });
  }, [isConnected, sendMessage]);

  /**
   * Get connection diagnostics
   */
  const getDiagnostics = useCallback(() => {
    return {
      connectionState,
      isConnected,
      error,
      metrics,
      subscriptions: Array.from(subscriptionsRef.current).length,
      messageHistory: messageHistory.length,
      wsManagerState: WebSocketManager.getState()
    };
  }, [connectionState, isConnected, error, metrics, messageHistory]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    const currentSubscriptions = subscriptionsRef.current;
    
    return () => {
      // Clean up subscriptions
      currentSubscriptions.forEach(unsubscribeFunc => {
        if (typeof unsubscribeFunc === 'function') {
          unsubscribeFunc();
        }
      });
      
      // Clear intervals
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, []);

  return {
    // Connection state
    connectionState,
    isConnected,
    error,
    metrics,
    
    // Connection methods
    connect,
    disconnect,
    
    // Messaging
    sendMessage,
    sendTradingMessage,
    lastMessage,
    messageHistory,
    
    // Subscriptions
    subscribe,
    unsubscribe,
    subscribeTopic,
    subscribeToMarketData,
    subscribeToOrderUpdates,
    subscribeToPortfolioUpdates,
    
    // Utilities
    ping,
    getDiagnostics
  };
}

/**
 * Hook for page-specific WebSocket functionality
 */
export function usePageWebSocket(pageId, options = {}) {
  return useWebSocket({
    ...options,
    page_id: pageId
  });
}

/**
 * Hook for trading-specific WebSocket functionality
 */
export function useTradingWebSocket(options = {}) {
  const webSocket = useWebSocket({
    ...options,
    page_id: 'trading',
    channels: ['trading', 'order_updates', 'market_data']
  });

  const [orders, setOrders] = useState([]);
  const [marketData, setMarketData] = useState({});
  const [portfolio, setPortfolio] = useState(null);

  // Handle trading-specific messages
  useEffect(() => {
    if (webSocket.isConnected) {
      // Subscribe to order updates
      const unsubscribeOrders = webSocket.subscribeToOrderUpdates((message) => {
        if (message.type === MESSAGE_TYPES.ORDER_UPDATE) {
          setOrders(prev => {
            const orderId = message.d?.oid || message.d?.order_id;
            const existingIndex = prev.findIndex(order => order.id === orderId);
            
            if (existingIndex >= 0) {
              // Update existing order
              const updated = [...prev];
              updated[existingIndex] = { ...updated[existingIndex], ...message.d };
              return updated;
            } else {
              // Add new order
              return [message.d, ...prev];
            }
          });
        }
      });

      // Subscribe to portfolio updates
      const unsubscribePortfolio = webSocket.subscribeToPortfolioUpdates((message) => {
        if (message.type === MESSAGE_TYPES.PORTFOLIO_UPDATE) {
          setPortfolio(message.d);
        }
      });

      return () => {
        unsubscribeOrders();
        unsubscribePortfolio();
      };
    }
  }, [webSocket]);

  return {
    ...webSocket,
    orders,
    marketData,
    portfolio,
    setMarketData
  };
}

export default useWebSocket;
